import React, { useCallback, useContext, useEffect, useState } from 'react'
import { View, StyleSheet, ScrollView, Platform } from 'react-native'
import { Text, Button, Input, CheckBox, Radio, RadioGroup, Modal, Spinner, useTheme } from '@ui-kitten/components'
import * as Clipboard from 'expo-clipboard'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react-native'
import { apiTokensApi, apiTokensErrorCode } from '../../global/ApiTokens/apiTokensApi'
import {
  AUDIT_PAGE_SIZE,
  EXPIRY_CHOICES_DAYS,
  actionLabelKey,
  auditActor,
  nextAuditCursor,
  scopeLabelKey,
} from '../../global/ApiTokens/apiTokensView'
import { clientTitle, connectionLabelKey } from '../../global/AiAssistants/aiAssistantsView'
import { useAiAssistantsAvailable } from '../../global/AiAssistants/useAiAssistantsAvailable'
import { UserContext } from '../../global/UserProvider'

/**
 * ATL-1440 — Settings → API tokens. Admins issue the organization tokens AI agents use with Atlas
 * API v1, see each token's state, revoke one with a single confirmed tap, and read the audit log of
 * everything done with a token. The backend enforces every rule here (admin only, scopes that can
 * be issued, the Supabase-organization gate); the screen only shows what it says.
 */
export default function ApiTokensScreen({ navigation }) {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const [context] = useContext(UserContext)
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [events, setEvents] = useState([])
  const [auditCursor, setAuditCursor] = useState(null)
  const [auditLoading, setAuditLoading] = useState(false)

  const [formOpen, setFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState(['read'])
  const [expiresInDays, setExpiresInDays] = useState(90)
  const [formError, setFormError] = useState(null)
  const [issuing, setIssuing] = useState(false)
  const [issued, setIssued] = useState(null)
  const [copied, setCopied] = useState(false)

  const [revokeTarget, setRevokeTarget] = useState(null)
  const [revoking, setRevoking] = useState(false)
  const [revokeError, setRevokeError] = useState(null)

  const formatDate = (value) => (value ? format(new Date(value), 'dd.MM.yyyy HH:mm') : '')
  const errorText = (error) => {
    const code = apiTokensErrorCode(error)
    if (code && i18n.exists(`apiTokens.error_${code}`)) return t(`apiTokens.error_${code}`)
    return t('apiTokens.error_generic')
  }

  const loadAudit = useCallback(async (before) => {
    setAuditLoading(true)
    try {
      const page = await apiTokensApi.audit({ limit: AUDIT_PAGE_SIZE, before })
      setEvents((prev) => (before ? [...prev, ...page] : page))
      setAuditCursor(nextAuditCursor(page))
    } catch (error) {
      Sentry.captureException(error)
      setLoadError(errorText(error))
    } finally {
      setAuditLoading(false)
    }
    // errorText reads t/i18n: without them here an error would render in the language of the
    // first render. `t` and `i18n` change identity only when the language does.
  }, [t, i18n])

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      setData(await apiTokensApi.list())
    } catch (error) {
      Sentry.captureException(error)
      setLoadError(errorText(error))
    }
    await loadAudit(null)
  }, [loadAudit])

  useEffect(() => {
    load()
  }, [load])

  const toggleScope = (scope) => {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]))
  }

  const issue = async () => {
    if (name.trim().length === 0) {
      setFormError(t('apiTokens.nameRequired'))
      return
    }
    setIssuing(true)
    setFormError(null)
    try {
      const result = await apiTokensApi.create({ name: name.trim(), scopes, expiresInDays })
      setIssued(result)
      setCopied(false)
      setFormOpen(false)
      setName('')
      setScopes(['read'])
      await load()
    } catch (error) {
      Sentry.captureException(error)
      setFormError(errorText(error))
    } finally {
      setIssuing(false)
    }
  }

  const copySecret = async () => {
    if (!issued) return
    await Clipboard.setStringAsync(issued.secret)
    setCopied(true)
  }

  const confirmRevoke = async () => {
    if (!revokeTarget) return
    setRevoking(true)
    setRevokeError(null)
    try {
      await apiTokensApi.revoke(revokeTarget.id)
      setRevokeTarget(null)
      await load()
    } catch (error) {
      Sentry.captureException(error)
      setRevokeError(errorText(error))
    } finally {
      setRevoking(false)
    }
  }

  const card = [styles.card, { backgroundColor: theme['background-basic-color-1'] }]
  const availability = data?.availability
  const canIssue = availability?.available === true
  const aiAssistantsAvailable = useAiAssistantsAvailable(canIssue, context?.user?.organizationId)

  if (!data && !loadError) {
    return (
      <View style={styles.center}>
        <Spinner />
      </View>
    )
  }

  return (
    <ScrollView style={styles.container} testID='api-tokens-screen'>
      <View style={card}>
        <Text category='s1' style={styles.gap}>{t('apiTokens.title')}</Text>
        <Text appearance='hint'>{t('apiTokens.intro')}</Text>
        {availability && !availability.available && (
          <Text status='warning' style={styles.topGap} testID='api-tokens-unavailable'>
            {availability.reason === 'organization_access_undetermined'
              ? t('apiTokens.undetermined')
              : t('apiTokens.notAvailable')}
          </Text>
        )}
        {loadError && <Text status='danger' style={styles.topGap}>{loadError}</Text>}
        {/* ATL-1694: Claude / ChatGPT connect through OAuth, not a pasted token — the guide says how,
            once the server says this organization can connect one. */}
        {aiAssistantsAvailable && (
          <Button
            size='small'
            appearance='outline'
            onPress={() => navigation.navigate('AiAssistants')}
            style={[styles.topGap, styles.alignStart]}
            testID='api-tokens-ai-help'
          >
            {t('aiAssistants.openHelp')}
          </Button>
        )}
      </View>

      {issued && (
        <View style={card} testID='api-tokens-secret'>
          <Text category='s1' style={styles.gap}>{t('apiTokens.secretTitle')}</Text>
          <Text status='warning' style={styles.gap}>{t('apiTokens.secretWarning')}</Text>
          <Text selectable style={[styles.secret, { backgroundColor: theme['background-basic-color-2'] }]}>
            {issued.secret}
          </Text>
          <View style={styles.row}>
            <Button size='small' onPress={copySecret} style={styles.rowButton} testID='api-tokens-copy'>
              {copied ? t('apiTokens.copied') : t('apiTokens.copy')}
            </Button>
            <Button size='small' status='basic' onPress={() => setIssued(null)} style={styles.rowButton}>
              {t('apiTokens.done')}
            </Button>
          </View>
        </View>
      )}

      {canIssue && !formOpen && !issued && (
        <Button onPress={() => setFormOpen(true)} style={styles.topGap} testID='api-tokens-create'>
          {t('apiTokens.create')}
        </Button>
      )}

      {formOpen && (
        <View style={card} testID='api-tokens-form'>
          <Input
            label={t('apiTokens.name')}
            placeholder={t('apiTokens.namePlaceholder')}
            value={name}
            onChangeText={setName}
            maxLength={100}
            style={styles.gap}
            testID='api-tokens-name'
          />
          <Text category='label' appearance='hint' style={styles.gap}>{t('apiTokens.scopes')}</Text>
          {data.allScopes.map((scope) => {
            const issuable = data.issuableScopes.includes(scope)
            return (
              <CheckBox
                key={scope}
                checked={scopes.includes(scope)}
                disabled={!issuable}
                onChange={() => toggleScope(scope)}
                style={styles.gap}
              >
                {t(scopeLabelKey(scope)) + (issuable ? '' : ` (${t('apiTokens.scopeSoon')})`)}
              </CheckBox>
            )
          })}
          <Text category='label' appearance='hint' style={[styles.gap, styles.topGap]}>{t('apiTokens.expiresIn')}</Text>
          <RadioGroup
            selectedIndex={EXPIRY_CHOICES_DAYS.indexOf(expiresInDays)}
            onChange={(index) => setExpiresInDays(EXPIRY_CHOICES_DAYS[index])}
          >
            {EXPIRY_CHOICES_DAYS.map((days) => (
              <Radio key={days}>{t('apiTokens.days', { count: days })}</Radio>
            ))}
          </RadioGroup>
          {formError && <Text status='danger' style={styles.topGap}>{formError}</Text>}
          <View style={styles.row}>
            <Button
              onPress={issue}
              disabled={issuing || scopes.length === 0}
              style={styles.rowButton}
              testID='api-tokens-issue'
            >
              {t('apiTokens.issue')}
            </Button>
            <Button status='basic' onPress={() => setFormOpen(false)} disabled={issuing} style={styles.rowButton}>
              {t('apiTokens.cancel')}
            </Button>
          </View>
        </View>
      )}

      <View style={card}>
        {data?.tokens.length === 0 && <Text appearance='hint'>{t('apiTokens.noTokens')}</Text>}
        {data?.tokens.map((token) => (
          <View key={token.id} style={styles.token} testID={`api-token-${token.id}`}>
            <View style={styles.tokenHeader}>
              <Text category='s2' style={styles.flex}>
                {token.kind === 'oauth' ? t(connectionLabelKey(token.client)) : token.name}
              </Text>
              <Text
                category='c1'
                status={token.status === 'active' ? 'success' : 'basic'}
              >
                {t(`apiTokens.status_${token.status}`)}
              </Text>
            </View>
            {token.kind === 'oauth' && token.client && (
              <Text category='c1' appearance='hint' testID={`api-token-client-${token.id}`}>
                {t('aiAssistants.connectionHost', { host: token.client.host })}
              </Text>
            )}
            <Text category='c1' appearance='hint'>
              {`${token.tokenPrefix}… · ${token.scopes.map((s) => t(scopeLabelKey(s))).join(', ')}`}
            </Text>
            <Text category='c1' appearance='hint'>
              {t('apiTokens.expires', { date: formatDate(token.expiresAt) })}
              {' · '}
              {token.lastUsedAt
                ? t('apiTokens.lastUsed', { date: formatDate(token.lastUsedAt) })
                : t('apiTokens.neverUsed')}
            </Text>
            <Text category='c1' appearance='hint'>
              {t('apiTokens.createdBy', { name: token.createdBy.name ?? '—' })}
            </Text>
            {token.status === 'active' && (
              <Button
                size='tiny'
                status='danger'
                appearance='outline'
                onPress={() => {
                  setRevokeError(null)
                  setRevokeTarget(token)
                }}
                style={styles.revoke}
                testID={`api-token-revoke-${token.id}`}
              >
                {t('apiTokens.revoke')}
              </Button>
            )}
          </View>
        ))}
      </View>

      <View style={card} testID='api-tokens-audit'>
        <Text category='s1' style={styles.gap}>{t('apiTokens.auditTitle')}</Text>
        {events.length === 0 && !auditLoading && <Text appearance='hint'>{t('apiTokens.auditEmpty')}</Text>}
        {events.map((event) => {
          const actor = auditActor(event)
          const labelKey = actionLabelKey(event.action, (key) => i18n.exists(key))
          return (
            <View key={event.id} style={styles.event}>
              <Text category='c1' appearance='hint'>{formatDate(event.occurredAt)}</Text>
              <Text category='p2'>
                {`${t(actor.key, { name: actor.name })} ${labelKey ? t(labelKey) : event.action}`}
                {event.outcome !== 'success' ? ` — ${t(`apiTokens.outcome_${event.outcome}`)}` : ''}
              </Text>
            </View>
          )
        })}
        {auditLoading && <Spinner size='small' />}
        {auditCursor && !auditLoading && (
          <Button size='small' appearance='ghost' onPress={() => loadAudit(auditCursor)}>
            {t('apiTokens.loadMore')}
          </Button>
        )}
      </View>

      <Modal
        visible={revokeTarget !== null}
        backdropStyle={styles.backdrop}
        onBackdropPress={() => !revoking && setRevokeTarget(null)}
        style={styles.modal}
      >
        <View style={[styles.modalContent, { backgroundColor: theme['background-basic-color-1'] }]}>
          <Text category='h6' style={styles.gap}>{t('apiTokens.revokeTitle')}</Text>
          <Text style={styles.gap}>
            {revokeTarget?.kind === 'oauth' && revokeTarget.client
              ? t('aiAssistants.revokeConnectionText', { client: t(clientTitle(revokeTarget.client).key) })
              : t('apiTokens.revokeText', { name: revokeTarget?.name ?? '' })}
          </Text>
          {revokeError && <Text status='danger' style={styles.gap}>{revokeError}</Text>}
          <View style={styles.row}>
            <Button status='basic' onPress={() => setRevokeTarget(null)} disabled={revoking} style={styles.rowButton}>
              {t('apiTokens.cancel')}
            </Button>
            <Button
              status='danger'
              onPress={confirmRevoke}
              disabled={revoking}
              style={styles.rowButton}
              testID='api-tokens-revoke-confirm'
            >
              {t('apiTokens.revoke')}
            </Button>
          </View>
        </View>
      </Modal>
      <View style={{ height: 50 }} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  card: { borderRadius: 10, padding: 15, marginTop: 10 },
  gap: { marginBottom: 8 },
  topGap: { marginTop: 10 },
  row: { flexDirection: 'row', marginTop: 12 },
  rowButton: { marginRight: 10 },
  flex: { flex: 1 },
  alignStart: { alignSelf: 'flex-start' },
  secret: {
    padding: 10,
    borderRadius: 6,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  token: { paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: 'rgba(128,128,128,0.3)' },
  tokenHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 2 },
  revoke: { alignSelf: 'flex-start', marginTop: 6 },
  event: { marginBottom: 8 },
  backdrop: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
  modal: { width: '90%', maxWidth: 420 },
  modalContent: { borderRadius: 10, padding: 20 },
})
